import PropTypes from "prop-types";
import React, { useEffect, useState, Suspense } from "react";

import {
  Switch,
  BrowserRouter as Router,
  Route,
  Redirect,
} from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
// Import Routes all
import { nonDomainRoutes, routes } from "./routes/allRoutes";
// Import scss
import "./assets/scss/theme.scss";
import "./assets/scss/preloader.scss";
import "react-toastify/dist/ReactToastify.css";

import { ToastContainer } from "react-toastify";
import { GlobalProvider } from "store/event/globalContext";
import Upload from "@/components/Upload/upload_image_v1";
import { User } from "pages/Authentication/Login";
import Error from "@/components/Errors/Error";
import { CartProvider } from "./store/cartContext";
import PWAInstallPrompt from "components/PWA/PWAInstallPrompt";
import GoogleAnalytics from "components/GoogleAnalytics";
import useWebNotification from "./hooks/useWebNotification";
import { TenantProvider } from "./providers/TenantProvider";
import { isSiteFotoowlPattern } from "./routes/routingUtils";
import Loader from "@/components/Loader";
import { useEventStore } from "./store/event/eventStore";

const App = () => {
  const [globalStates, setGlobalStates] = useState({});

  const eventData = useEventStore((state) => state.eventData);

  // Initialize web notifications (registers service workers)
  useWebNotification();

  useEffect(() => {
    const host = new URL(window.location).host;
    const queryParams = new URLSearchParams(window.location.search);
    const debug_domain_pattern = queryParams.get("domain_pattern");

    const domain_pattern = host.includes("localhost")
      ? debug_domain_pattern
      : host;
    if (!domain_pattern) {
      console.debug("add domain_pattern from query params for localhost");
    }
    setGlobalStates({ domain_pattern });
  }, []);

  // Disable right click globally to prevent downloading of images with save image as  
  useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault();
    };

    document.addEventListener("contextmenu", handleContextMenu);

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
    };
  }, []);

  // Extract eventId from URL pathname
  const eventId = window.location.pathname.split("/").pop();
  const shouldShowPWA =
    eventId &&
    eventId !== "182491" &&
    !isNaN(eventId) &&
    eventData?.pwa_enabled;

  return (
    <React.Fragment>
      <ToastContainer
        autoClose={2000}
        position="bottom-right"
        hideProgressBar={true}
        className={"custom-toast"}
        theme={"dark"}
        draggable
        pauseOnHover
        limit={1}
      />

      {shouldShowPWA && (
        <>
          {window.os !== "android" && window.os !== "ios" && (
            <PWAInstallPrompt />
          )}
        </>
      )}
      <ErrorBoundary FallbackComponent={Error}>
        <User>
          <Upload>
            <GlobalProvider value={globalStates}>
              <CartProvider>
                <Router>
                  <GoogleAnalytics />

                  <Switch>
                    {nonDomainRoutes.map((route, idx) => (
                      <Route
                        key={`non-domain-route-${idx}`}
                        exact
                        path={route.path}
                        component={route.component}
                      />
                    ))}
                    {/* 1) Path-based tenant: site.fotoowl.ai/:tenant/... 
                        ALL routes go through this pattern when path-based */}
                    {isSiteFotoowlPattern() && (
                      <Route
                        path="/:tenant"
                        render={({ match }) => {
                          // Only render if we're actually on site.fotoowl.ai pattern
                          // This prevents matching subdomain routes incorrectly
                          if (!isSiteFotoowlPattern()) {
                            return null;
                          }

                          return (
                            <TenantProvider
                              initialTenantFromPath={match.params.tenant}
                              isPathBased={true}
                            >
                              <Suspense
                                fallback={<Loader className="ht-100 w-100" />}
                              >
                                <Switch>
                                  {/* Regular routes with tenant prefix */}
                                  {routes.map((route, idx) => (
                                    <Route
                                      key={`tenant-route-${idx}`}
                                      exact={route.path === "/"}
                                      path={`${match.path}${route.path === "/" ? "" : route.path
                                        }`}
                                      component={route.component}
                                    />
                                  ))}
                                  <Redirect to={match.path} />
                                </Switch>
                              </Suspense>
                            </TenantProvider>
                          );
                        }}
                      />
                    )}

                    {/* 2) Subdomain-based and custom domain tenant: <tenant>.fotoowl.ai/... or tenant.com/... 
                        ALL routes go through this pattern when subdomain-based
                        We mount TenantProvider at root; it will inspect hostname */}
                    {!isSiteFotoowlPattern() && (
                      <Route
                        path="/"
                        render={() => (
                          <TenantProvider>
                            <Suspense
                              fallback={<Loader className="ht-100 w-100" />}
                            >
                              <Switch>
                                {/* Regular routes without tenant prefix */}
                                {routes.map((route, idx) => (
                                  <Route
                                    key={`subdomain-route-${idx}`}
                                    exact={route.path === "/"}
                                    path={route.path}
                                    component={route.component}
                                  />
                                ))}
                                <Redirect to="/" />
                              </Switch>
                            </Suspense>
                          </TenantProvider>
                        )}
                      />
                    )}
                  </Switch>
                </Router>
              </CartProvider>
            </GlobalProvider>
          </Upload>
        </User>
      </ErrorBoundary>
    </React.Fragment>
  );
};

App.propTypes = {
  layout: PropTypes.any,
};

export default App;
